/**
 * What a plan period REALLY owes. `amount`/`amountPaid` are rent (+GST) only; the
 * refundable security deposit is billed on a membership's first period and is
 * tracked separately (`depositDue` / `depositReceived`). The order receipt's
 * TOTAL TO BE PAID includes it, so the Plan tab's Pending must too — otherwise a
 * plan with an unpaid deposit shows "Pending ₹571" while the receipt says ₹4,907.
 */
export interface MoneyPeriod {
  amount: number;
  amountPaid?: number | null;
  depositDue?: number | null;
  depositReceived?: number | null;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

export function periodMoney(p: MoneyPeriod) {
  const rent = Number(p.amount ?? 0);
  const rentPaid = Number(p.amountPaid ?? 0);
  const depositDue = Number(p.depositDue ?? 0);
  // Never credit more deposit than is due.
  const depositPaid = Math.min(depositDue, Number(p.depositReceived ?? 0));
  const total = r2(rent + depositDue);
  const paid = r2(rentPaid + depositPaid);
  return {
    rent,
    depositDue,
    depositPending: Math.max(0, r2(depositDue - depositPaid)),
    total,
    paid,
    pending: Math.max(0, r2(total - paid)),
  };
}

export function sumPeriodMoney(ps: readonly MoneyPeriod[]) {
  return ps.reduce(
    (acc, p) => {
      const m = periodMoney(p);
      acc.depositDue = r2(acc.depositDue + m.depositDue);
      acc.depositPending = r2(acc.depositPending + m.depositPending);
      acc.total = r2(acc.total + m.total);
      acc.paid = r2(acc.paid + m.paid);
      acc.pending = r2(acc.pending + m.pending);
      return acc;
    },
    { depositDue: 0, depositPending: 0, total: 0, paid: 0, pending: 0 },
  );
}
